import type { HTMLAttributes, ReactElement, ReactNode, Ref } from "react";
import classNames from "classnames";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import type { MenuRowSlots } from "./Menu.types";
import styles from "./MenuItem.module.scss";

type MenuItemContentProps = Pick<
    MenuRowSlots,
    "children" | "leading" | "description" | "rightText"
> & {
    /** Trailing glyph or control after the right text (submenu triangle, check, switch). */
    trailing?: ReactNode;
};

/**
 * The row anatomy every item-shaped part shares: leading slot, label over an
 * optional description, then the trailing cluster (right text and a glyph).
 */
export const MenuItemContent = ({
    children,
    leading,
    description,
    rightText,
    trailing,
}: MenuItemContentProps) => (
    <>
        {leading != null && (
            <span className={styles.itemLeading}>{leading}</span>
        )}
        <span className={styles.itemBody}>
            <span className={styles.itemLabel}>{children}</span>
            {description != null && (
                <span className={styles.itemDescription}>{description}</span>
            )}
        </span>
        {(rightText != null || trailing != null) && (
            <span className={styles.itemTrailing}>
                {rightText != null && (
                    <span className={styles.itemRightText}>{rightText}</span>
                )}
                {trailing}
            </span>
        )}
    </>
);

/** Class list of an item-shaped row; the consumer's `className` merges last. */
export const menuItemClassName = ({
    hasDescription,
    isDestructive,
    className,
}: {
    hasDescription: boolean;
    isDestructive: boolean;
    className?: string;
}) =>
    classNames(
        styles.item,
        {
            [styles.itemWithDescription]: hasDescription,
            [styles.itemDestructive]: isDestructive,
        },
        className,
    );

type RowElementProps = HTMLAttributes<HTMLDivElement> & {
    ref?: Ref<HTMLDivElement>;
};

/**
 * Base UI `render` for a row with a hover tooltip: `disabledReason` while
 * disabled (nothing without one), otherwise `tooltip`. It opens beside the row (inline-start,
 * flipping when there is no room) so it never covers a neighbouring row, as
 * Picker rows do. The tooltip wraps the element Base UI renders: a disabled
 * row only carries `aria-disabled`, so it keeps receiving hover and focus.
 * Every row renders through the same Tooltip (it disables itself when there
 * is nothing to show), so a reason or tooltip that comes, goes or switches
 * never remounts the row and never drops its keyboard focus.
 */
export const renderRowWithTooltip = ({
    isDisabled,
    disabledReason,
    tooltip,
}: {
    isDisabled: boolean;
    disabledReason: ReactNode;
    tooltip?: ReactNode;
}): ((props: RowElementProps) => ReactElement) => {
    const content = isDisabled ? (disabledReason ?? null) : (tooltip ?? null);
    return (props: RowElementProps) => (
        <Tooltip content={content} side="inline-start">
            <div {...props} />
        </Tooltip>
    );
};
